import clsx from "clsx";
import { FC, Ref, useEffect, useRef } from "react";

import { TermType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { LegacyButton } from "mds/components/LegacyButton";
import {
  ClickEvent,
  Menu,
  MenuDirection,
  MenuInstance,
  MenuItem,
  MenuType,
  SubMenu,
} from "mds/components/Menu";
import { CalendarIcon, ChevronDownIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectSortedTerms, selectSortedTermsForCurrentUser } from "store/selectors";

export type TermMenuProps = {
  selectedTermId: string;
  menuClassName?: string;
  onTermChange: (termId: string) => void;
  menuType?: MenuType;
  showIcons?: boolean;
  size?: "xs" | "s";
  defaultToAll?: boolean;
  eventPrefix?: string;
  filterToCurrentUser?: boolean;
  direction?: MenuDirection;
};

export const TermMenuText = ({ text, showIcons }: { text: string; showIcons?: boolean }) =>
  showIcons ? <IconText iconStart={<CalendarIcon />} text={text} /> : text;

export const TermMenu: FC<TermMenuProps> = ({
  selectedTermId,
  onTermChange,
  menuClassName,
  menuType = "submenu",
  showIcons = true,
  defaultToAll = true,
  size = "xs",
  eventPrefix = "",
  filterToCurrentUser = false,
  direction = "bottom",
}) => {
  const subMenuRef: Ref<MenuInstance> = useRef(null);
  const allTerms = useAppSelector(selectSortedTerms);
  const termsForCurrentUser = useAppSelector(selectSortedTermsForCurrentUser);
  const terms = filterToCurrentUser ? termsForCurrentUser : allTerms;
  const defaultText = defaultToAll ? t("import_modal.all_terms") : t("import_modal.select_term");
  const termOptions = [{ id: "", title: defaultText }, ...terms];

  const selectedTerm = termOptions.find((term) => term.id === selectedTermId);

  useEffect(() => {
    storeApi.terms.list();
  }, []);

  const onItemClick = (e: ClickEvent) => {
    const newTerm = e.value as TermType;
    onTermChange(newTerm.id);
    // This is a supported feature of szhsin/react-menu which allows
    // the menu to stay open even if a menu item is clicked. We use this to keep parent menus open
    // eslint-disable-next-line no-param-reassign
    e.keepOpen = true;
    subMenuRef.current.closeMenu();
  };

  const menuButton = (
    <LegacyButton className="gap-1" kind="secondary" size={size}>
      <TermMenuText showIcons={showIcons} text={selectedTerm?.title} />
      <ChevronDownIcon />
    </LegacyButton>
  );

  const DropdownMenu = termOptions.map((termOption) => {
    const selected = selectedTerm?.id === termOption.id;
    return (
      <MenuItem
        checked={selected}
        className={clsx({ selected, "text-white": selected }, "center dropdown-item")}
        eventName={`${eventPrefix}Term selected`}
        eventProps={{ term: termOption.title }}
        key={termOption.id}
        value={termOption}
        onClick={onItemClick}
      >
        <TermMenuText showIcons={showIcons} text={termOption.title} />
      </MenuItem>
    );
  });

  const menuProps = {
    className: "dropdown",
    instanceRef: subMenuRef,
    children: DropdownMenu,
  };

  return menuType === "menu" ? (
    <Menu
      {...menuProps}
      direction={direction}
      menuButton={menuButton}
      menuClassName={menuClassName}
      position="initial"
    />
  ) : (
    <SubMenu
      {...menuProps}
      direction={direction}
      label={menuButton}
      openTrigger="clickOnly"
      position="initial"
    />
  );
};
